import { isVmManagedSecretName } from "@t3tools/contracts";

export type VmSecretEntry = {
  readonly name: string;
  readonly value: string;
};

export type VmSecretParseErrorCode =
  | "duplicate"
  | "invalid-name"
  | "missing-assignment"
  | "missing-name"
  | "reserved-name"
  | "unterminated-value"
  | "unexpected-value-suffix";

export type VmSecretParseError = {
  readonly code: VmSecretParseErrorCode;
  readonly message: string;
};

export type VmSecretDraftRow = VmSecretEntry & {
  readonly lineNumber: number;
  readonly error: VmSecretParseError | null;
};

export type VmSecretParseResult = {
  readonly rows: ReadonlyArray<VmSecretDraftRow>;
  readonly entries: ReadonlyArray<VmSecretEntry>;
  readonly errorCount: number;
};

const SECRET_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;
const MAX_SECRET_NAME_LENGTH = 256;

function parseDoubleQuotedValue(value: string): string {
  let parsed = "";
  for (let index = 0; index < value.length; index += 1) {
    const character = value[index];
    if (character !== "\\" || index === value.length - 1) {
      parsed += character;
      continue;
    }

    const escaped = value[index + 1];
    switch (escaped) {
      case "n":
        parsed += "\n";
        break;
      case "r":
        parsed += "\r";
        break;
      case "t":
        parsed += "\t";
        break;
      case '"':
      case "\\":
        parsed += escaped;
        break;
      default:
        parsed += `\\${escaped}`;
        break;
    }
    index += 1;
  }
  return parsed;
}

function parseQuotedValue(
  input: string,
  quote: '"' | "'",
): { readonly value: string; readonly error: VmSecretParseError | null } {
  let escaped = false;
  let closingQuote = -1;

  for (let index = 1; index < input.length; index += 1) {
    const character = input[index];
    if (quote === '"' && character === "\\" && !escaped) {
      escaped = true;
      continue;
    }
    if (character === quote && !escaped) {
      closingQuote = index;
      break;
    }
    escaped = false;
  }

  if (closingQuote === -1) {
    return {
      value: input.slice(1),
      error: { code: "unterminated-value", message: "Close the quoted secret value." },
    };
  }

  const suffix = input.slice(closingQuote + 1).trim();
  if (suffix.length > 0 && !suffix.startsWith("#")) {
    return {
      value: input.slice(1, closingQuote),
      error: {
        code: "unexpected-value-suffix",
        message: "Only a comment can follow a quoted secret value.",
      },
    };
  }

  const quotedValue = input.slice(1, closingQuote);
  return {
    value: quote === '"' ? parseDoubleQuotedValue(quotedValue) : quotedValue,
    error: null,
  };
}

function parseSecretValue(input: string): {
  readonly value: string;
  readonly error: VmSecretParseError | null;
} {
  const value = input.trimStart();
  if (value.startsWith('"') || value.startsWith("'")) {
    return parseQuotedValue(value, value[0] as '"' | "'");
  }

  const commentIndex = value.indexOf("#");
  return {
    value: (commentIndex === -1 ? value : value.slice(0, commentIndex)).trim(),
    error: null,
  };
}

export function getVmSecretNameError(name: string): VmSecretParseError | null {
  if (name.length === 0) {
    return { code: "missing-name", message: "Enter a secret name before =." };
  }
  if (name.length > MAX_SECRET_NAME_LENGTH || !SECRET_NAME_PATTERN.test(name)) {
    return {
      code: "invalid-name",
      message: "Start with a letter or underscore, then use letters, numbers, or underscores.",
    };
  }
  if (isVmManagedSecretName(name)) {
    return {
      code: "reserved-name",
      message: `${name} is managed by the VM. Choose another name.`,
    };
  }
  return null;
}

function parseLine(line: string, lineNumber: number): VmSecretDraftRow | null {
  let assignment = line.trim();
  if (assignment.length === 0 || assignment.startsWith("#")) return null;

  if (/^export\s/.test(assignment)) {
    assignment = assignment.replace(/^export\s+/, "");
  }

  const equalsIndex = assignment.indexOf("=");
  if (equalsIndex === -1) {
    return {
      lineNumber,
      name: assignment.trim(),
      value: "",
      error: { code: "missing-assignment", message: "Use NAME=value." },
    };
  }

  const name = assignment.slice(0, equalsIndex).trim();
  const nameError = getVmSecretNameError(name);
  const parsedValue = parseSecretValue(assignment.slice(equalsIndex + 1));

  return {
    lineNumber,
    name,
    value: parsedValue.value,
    error: nameError ?? parsedValue.error,
  };
}

export function parseVmSecrets(input: string): VmSecretParseResult {
  const parsedRows = input
    .split(/\r?\n/)
    .map((line, index) => parseLine(line, index + 1))
    .filter((row): row is VmSecretDraftRow => row !== null);
  const nameCounts = new Map<string, number>();
  for (const row of parsedRows) {
    if (row.name.length > 0) nameCounts.set(row.name, (nameCounts.get(row.name) ?? 0) + 1);
  }

  const rows = parsedRows.map((row): VmSecretDraftRow => {
    if (row.error !== null) return row;
    if ((nameCounts.get(row.name) ?? 0) > 1) {
      return {
        ...row,
        error: { code: "duplicate", message: `${row.name} appears more than once.` },
      };
    }
    return row;
  });

  const entries = rows
    .filter((row) => row.error === null)
    .map(({ name, value }) => ({ name, value }));
  return {
    rows,
    entries,
    errorCount: rows.length - entries.length,
  };
}
